import { Link } from "react-router-dom";
import { MessageCircle, Phone, Instagram, Facebook, Youtube } from "lucide-react";
import { PHONE_DISPLAY, PHONE_NUMBER, WHATSAPP_NUMBER, INSTAGRAM_URL, FACEBOOK_URL, YOUTUBE_URL } from "@/lib/cart";
import { useLang } from "@/lib/i18n";
import { asset } from "@/lib/siteAssets";
import { track, trackNavClick } from "@/lib/analytics";

const SOCIAL = [
  { href: INSTAGRAM_URL, icon: Instagram, label: "Instagram" },
  { href: FACEBOOK_URL,  icon: Facebook,  label: "Facebook"  },
  { href: YOUTUBE_URL,   icon: Youtube,   label: "YouTube"   },
];

const WA_GREETING = encodeURIComponent(
  "Hello Annapurna Home Pickles, I would like to place an order."
);

export const Footer = () => {
  const { t } = useLang();
  return (
    <footer data-testid="site-footer" className="relative bg-[#5A2D1D] text-[#F5E6CC] mt-10">
      <div className="brand-divider opacity-100 !bg-[#556B2F]" />

      {/* ── Mobile: compact horizontal layout ── */}
      <div className="lg:hidden px-4 py-5">
        {/* Brand row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={asset("brand/logo-v2.webp")} alt="Annapurna Home Pickles" loading="lazy" decoding="async" className="w-8 h-8 rounded-full object-cover" />
            <div>
              <div className="font-display text-base font-bold leading-tight">Annapurna</div>
              <div className="text-[9px] tracking-[0.2em] uppercase text-[#556B2F] font-semibold">Home Pickles</div>
            </div>
          </div>
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WA_GREETING}`}
            data-testid="footer-whatsapp-order"
            target="_blank"
            rel="noreferrer"
            onClick={() => track("whatsapp_contact_clicked", { source: "footer_reach_us" })}
            className="inline-flex items-center gap-1.5 bg-[#556B2F] hover:bg-[#6a8639] text-[#F5E6CC] px-3 py-2 rounded-full font-semibold text-xs transition-colors"
          >
            <MessageCircle className="w-3.5 h-3.5" />
            {t.contact.whatsapp}
          </a>
        </div>
        <div className="mt-2">
          <Link
            to="/track-order"
            data-testid="footer-track-order-link-mobile"
            onClick={() => trackNavClick("Track Your Order", "footer_cta")}
            className="inline-flex items-center gap-1.5 border border-[#F5E6CC]/25 hover:border-[#F5E6CC]/55 text-[#F5E6CC]/80 hover:text-[#F5E6CC] px-3 py-1.5 rounded-full font-semibold text-xs transition-colors"
          >
            Track Your Order
          </Link>
        </div>

        {/* Nav links — horizontal wrap */}
        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-[#F5E6CC]/75">
          <Link to="/" onClick={() => trackNavClick("Home", "footer_nav")} className="hover:text-[#F5E6CC]">{t.nav.home}</Link>
          <Link to="/about" onClick={() => trackNavClick("About", "footer_nav")} className="hover:text-[#F5E6CC]">{t.nav.about}</Link>
          <Link to="/products" onClick={() => trackNavClick("Products", "footer_nav")} className="hover:text-[#F5E6CC]">{t.nav.products}</Link>
          <Link to="/contact" onClick={() => trackNavClick("Contact", "footer_nav")} className="hover:text-[#F5E6CC]">{t.nav.contact}</Link>
          <Link to="/offers" onClick={() => trackNavClick("Offers", "footer_nav")} className="hover:text-[#F5E6CC]">{t.nav.offers}</Link>
          <Link to="/my-orders" onClick={() => trackNavClick("My Orders", "footer_nav")} className="hover:text-[#F5E6CC]">{t.nav.my_orders}</Link>
        </div>

        {/* Policy links — mobile */}
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-[#F5E6CC]/75">
          <Link to="/terms-and-conditions" target="_blank" rel="noreferrer" onClick={() => trackNavClick("Terms & Conditions", "footer_policy")} className="hover:text-[#F5E6CC]">Terms & Conditions</Link>
          <Link to="/privacy-policy" target="_blank" rel="noreferrer" onClick={() => trackNavClick("Privacy Policy", "footer_policy")} className="hover:text-[#F5E6CC]">Privacy Policy</Link>
          <Link to="/shipping-policy" target="_blank" rel="noreferrer" onClick={() => trackNavClick("Shipping Policy", "footer_policy")} className="hover:text-[#F5E6CC]">Shipping Policy</Link>
          <Link to="/refund-policy" target="_blank" rel="noreferrer" onClick={() => trackNavClick("Refund Policy", "footer_policy")} className="hover:text-[#F5E6CC]">Refund Policy</Link>
          <Link to="/return-policy" target="_blank" rel="noreferrer" onClick={() => trackNavClick("Return Policy", "footer_policy")} className="hover:text-[#F5E6CC]">Return Policy</Link>
          <Link to="/cancellation-policy" target="_blank" rel="noreferrer" onClick={() => trackNavClick("Cancellation Policy", "footer_policy")} className="hover:text-[#F5E6CC]">Cancellation Policy</Link>
        </div>

        {/* Contact + social row */}
        <div className="mt-3 flex items-center justify-between">
          <a href={`tel:+${PHONE_NUMBER}`} onClick={() => track("call_us_clicked", { source: "footer_reach_us" })} className="flex items-center gap-1.5 text-xs text-[#F5E6CC]/75 hover:text-[#F5E6CC]">
            <Phone className="w-3.5 h-3.5" /> {PHONE_DISPLAY}
          </a>
          <div className="flex items-center gap-3">
            {SOCIAL.map(({ href, icon: Icon, label }) => (
              <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label}
                onClick={() => trackNavClick(label, "footer_reach_us")}
                className="text-[#F5E6CC]/60 hover:text-[#F5E6CC] transition-colors">
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-4 pt-3 border-t border-[#F5E6CC]/15 text-[10px] text-[#F5E6CC]/50">
          {t.footer.rights}
        </div>
      </div>

      {/* ── Desktop: compact 4-column grid ── */}
      <div className="hidden lg:block max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3">
              <img src={asset("brand/logo-v2.webp")} alt="Annapurna Home Pickles" loading="lazy" decoding="async" className="w-10 h-10 rounded-full object-cover" />
              <div>
                <div className="font-display text-lg font-bold">Annapurna</div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#556B2F] font-semibold">Home Pickles</div>
              </div>
            </div>
            <p className="mt-3 text-sm text-[#F5E6CC]/70 max-w-md leading-relaxed">
              {t.about.tagline_1} {t.about.tagline_2}
            </p>
            <div className="mt-4 flex items-center gap-3">
              {SOCIAL.map(({ href, icon: Icon, label }) => (
                <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label}
                  onClick={() => trackNavClick(label, "footer_brand")}
                  className="w-8 h-8 rounded-full border border-[#F5E6CC]/20 hover:border-[#F5E6CC]/60 text-[#F5E6CC]/60 hover:text-[#F5E6CC] flex items-center justify-center transition-colors">
                  <Icon className="w-3.5 h-3.5" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#F5E6CC]/60 mb-3">{t.footer.explore}</div>
            <ul className="space-y-1.5 text-sm">
              <li><Link to="/" data-testid="footer-link-home" onClick={() => trackNavClick("Home", "footer_nav")} className="hover:text-[#F5E6CC] text-[#F5E6CC]/75">{t.nav.home}</Link></li>
              <li><Link to="/about" data-testid="footer-link-about" onClick={() => trackNavClick("About", "footer_nav")} className="hover:text-[#F5E6CC] text-[#F5E6CC]/75">{t.nav.about}</Link></li>
              <li><Link to="/products" data-testid="footer-link-products" onClick={() => trackNavClick("Products", "footer_nav")} className="hover:text-[#F5E6CC] text-[#F5E6CC]/75">{t.nav.products}</Link></li>
              <li><Link to="/contact" data-testid="footer-link-contact" onClick={() => trackNavClick("Contact", "footer_nav")} className="hover:text-[#F5E6CC] text-[#F5E6CC]/75">{t.nav.contact}</Link></li>
              <li><Link to="/offers" data-testid="footer-link-offers" onClick={() => trackNavClick("Offers", "footer_nav")} className="hover:text-[#F5E6CC] text-[#F5E6CC]/75">{t.nav.offers}</Link></li>
              <li><Link to="/my-orders" data-testid="footer-link-my-orders" onClick={() => trackNavClick("My Orders", "footer_nav")} className="hover:text-[#F5E6CC] text-[#F5E6CC]/75">{t.nav.my_orders}</Link></li>
            </ul>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#F5E6CC]/60 mb-3">Terms & Conditions</div>
            <ul className="space-y-1.5 text-sm">
              <li><Link to="/terms-and-conditions" target="_blank" rel="noreferrer" data-testid="footer-link-terms" onClick={() => trackNavClick("Terms & Conditions", "footer_policy")} className="hover:text-[#F5E6CC] text-[#F5E6CC]/75">Terms & Conditions</Link></li>
              <li><Link to="/privacy-policy" target="_blank" rel="noreferrer" data-testid="footer-link-privacy" onClick={() => trackNavClick("Privacy Policy", "footer_policy")} className="hover:text-[#F5E6CC] text-[#F5E6CC]/75">Privacy Policy</Link></li>
              <li><Link to="/shipping-policy" target="_blank" rel="noreferrer" data-testid="footer-link-shipping" onClick={() => trackNavClick("Shipping Policy", "footer_policy")} className="hover:text-[#F5E6CC] text-[#F5E6CC]/75">Shipping Policy</Link></li>
              <li><Link to="/refund-policy" target="_blank" rel="noreferrer" data-testid="footer-link-refund" onClick={() => trackNavClick("Refund Policy", "footer_policy")} className="hover:text-[#F5E6CC] text-[#F5E6CC]/75">Refund Policy</Link></li>
              <li><Link to="/return-policy" target="_blank" rel="noreferrer" data-testid="footer-link-return" onClick={() => trackNavClick("Return Policy", "footer_policy")} className="hover:text-[#F5E6CC] text-[#F5E6CC]/75">Return Policy</Link></li>
              <li><Link to="/cancellation-policy" target="_blank" rel="noreferrer" data-testid="footer-link-cancellation" onClick={() => trackNavClick("Cancellation Policy", "footer_policy")} className="hover:text-[#F5E6CC] text-[#F5E6CC]/75">Cancellation Policy</Link></li>
            </ul>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#F5E6CC]/60 mb-3">{t.footer.reach_us}</div>
            <a href={`tel:+${PHONE_NUMBER}`} data-testid="footer-phone" onClick={() => track("call_us_clicked", { source: "footer_reach_us" })} className="flex items-center gap-2 text-sm hover:text-[#F5E6CC] text-[#F5E6CC]/80 mb-2.5">
              <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
            </a>
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WA_GREETING}`}
              data-testid="footer-whatsapp-order"
              target="_blank"
              rel="noreferrer"
              onClick={() => track("whatsapp_contact_clicked", { source: "footer_reach_us" })}
              className="inline-flex items-center gap-2 bg-[#556B2F] hover:bg-[#6a8639] text-[#F5E6CC] px-4 py-2 rounded-full font-semibold text-sm transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              {t.contact.whatsapp}
            </a>
            <div className="mt-2">
              <Link
                to="/track-order"
                data-testid="footer-track-order-link"
                onClick={() => trackNavClick("Track Your Order", "footer_cta")}
                className="inline-flex items-center gap-1.5 border border-[#F5E6CC]/25 hover:border-[#F5E6CC]/55 text-[#F5E6CC]/80 hover:text-[#F5E6CC] px-3 py-1.5 rounded-full font-semibold text-xs transition-colors"
              >
                Track Your Order
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-[#F5E6CC]/15 flex justify-between gap-3 text-xs text-[#F5E6CC]/60">
          <div>{t.footer.rights}</div>
          <div>{t.footer.crafted}</div>
        </div>
      </div>
    </footer>
  );
};
